import type { Query, QueryClient, UseQueryOptions } from '@zg-rentals/react-query5';
import { tsClientOptions, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import {
  type SatelliteWebLandlordConversationGetMessagesRequestDto,
  SatelliteWebLandlordConversationHandler_getMessages,
} from '@zg-rentals/ts-satellite-client';
import {
  type ConversationMessage,
  type ConversationMessagesData,
  mapGetMessagesResponseToConversationData,
  markMessageAttachmentsDeleted,
} from '../utils/conversationMessageUtils';
import { is4xxError } from '../utils/errorUtils';

// `getErrorHttpStatus` now lives in `errorUtils` (shared with the inbox-list 4xx guard).
// Re-exported here for back-compat with callers/tests that import it from this module.
export { getErrorHttpStatus } from '../utils/errorUtils';

export const CONVERSATION_MESSAGES_TAG = 'satellite:conversationMessages';

/**
 * Custom query-key prefix for the wrapped `getMessages` call so we can store
 * the *mapped* `ConversationMessagesData` shape in cache (rather than the raw
 * DTO that the generated `useSatelliteWebLandlordConversationHandler_getMessages.getKey`
 * would index). Keeps cache writers (AddMessage, ListingMessage, etc.) simple.
 */
export const CONVERSATION_MESSAGES_QUERY_KEY_PREFIX = 'satellite:landlord:conversationMessages';

export type ConversationMessagesQueryKey = readonly [
  typeof CONVERSATION_MESSAGES_QUERY_KEY_PREFIX,
  SatelliteWebLandlordConversationGetMessagesRequestDto,
];

export function getConversationMessagesQueryKey(
  postData: SatelliteWebLandlordConversationGetMessagesRequestDto,
): ConversationMessagesQueryKey {
  return [CONVERSATION_MESSAGES_QUERY_KEY_PREFIX, postData] as const;
}

type UseConversationMessagesQueryOptions<TData = ConversationMessagesData> = {
  conversationId: string | undefined | null;
  clientTimeZone: string;
  /**
   * Polling interval in ms. Polling automatically stops on 4xx errors
   * (non-recoverable) so the UI doesn't hammer the endpoint indefinitely
   * for a stale or unauthorized conversation.
   */
  pollInterval?: number;
  enabled?: boolean;
  /**
   * Fetch `getMessages` once when the cache is empty, rather than never
   * (`enabled: false`) or always (`enabled: true`). Cross-surface consumers
   * (NotesModal, SendApplicationConfirmationModal, the RenterDetailsModal notepad)
   * also render on lead-management / contacts surfaces that have no `MessageListItems`
   * poller, so they self-fetch there; on the inbox the warm cache keeps them read-only.
   *
   * Because the observer stays disabled once the cache is warm, `invalidateQueries` on
   * this key only refetches where a poller is co-mounted (the inbox) — elsewhere it just
   * marks the entry stale.
   */
  fetchIfCacheEmpty?: boolean;
  /**
   * Narrow the cached `ConversationMessagesData` to just the slice a consumer
   * needs, so cache-only sidebar consumers re-render only when their slice
   * changes rather than on every 60s message poll.
   */
  select?: (data: ConversationMessagesData) => TData;
};

/**
 * Wrapper around the generated `getMessages` Proxit hook that:
 *  - Maps the raw response into the legacy `ConversationMessagesData` shape
 *    used by all MessageList consumers and cache writers.
 *  - Stops `refetchInterval` on 4xx errors (non-recoverable polling guard).
 *  - Preserves in-flight optimistic (`temp-…`) messages across polls so a
 *    sending / failed-send bubble survives the next refetch.
 *  - Attaches `meta.tags` / `meta.context` for cache invalidation + global
 *    error logging.
 */
export function useConversationMessagesQuery<TData = ConversationMessagesData>({
  conversationId,
  clientTimeZone,
  pollInterval,
  enabled = true,
  fetchIfCacheEmpty = false,
  select,
}: UseConversationMessagesQueryOptions<TData>) {
  const queryClient = useQueryClient();
  const postData: SatelliteWebLandlordConversationGetMessagesRequestDto = {
    conversationId: conversationId || '',
    clientTimeZone,
  };
  const queryKey = getConversationMessagesQueryKey(postData);
  const hasCachedData = fetchIfCacheEmpty && queryClient.getQueryData(queryKey) !== undefined;

  const queryOptions: UseQueryOptions<ConversationMessagesData, unknown, TData, ConversationMessagesQueryKey> = {
    // Spread the shared ts-client defaults first (notably `retry: false`) so 4xx errors
    // don't get retried 3× by React Query's default retry policy.
    ...tsClientOptions.queryOptions,
    // Returning to the tab must not re-issue getMessages for a conversation the user can't access
    // (it would re-hit the 403). SLW's query provider only sets `staleTime`, so the React Query
    // default (`refetchOnWindowFocus: true`) would otherwise apply.
    refetchOnWindowFocus: false,
    queryKey,
    queryFn: async () => {
      const response = await SatelliteWebLandlordConversationHandler_getMessages(postData);
      const fresh = mapGetMessagesResponseToConversationData(response);
      // React Query replaces the cache entry wholesale when a poll resolves; without
      // explicit merge logic, in-flight optimistic temps (sending / failed) would be
      // wiped from the UI on the next 60s tick. The previous Apollo query relied on
      // `refetchWritePolicy: 'merge'` for the same property. Preserve here instead.
      const previous = queryClient.getQueryData<ConversationMessagesData>(queryKey);
      return mergeInFlightTemps(previous, fresh);
    },
    enabled: enabled && !!conversationId && !hasCachedData,
    refetchInterval: (
      query: Query<ConversationMessagesData, unknown, ConversationMessagesData, ConversationMessagesQueryKey>,
    ) => computeRefetchInterval(query.state.error, pollInterval),
    select,
    meta: {
      tags: [CONVERSATION_MESSAGES_TAG],
      context: {
        api: 'getMessages',
        details: 'useConversationMessagesQuery - Error fetching conversation messages',
      },
    },
  };

  return useQuery(queryOptions);
}

/**
 * Patch a cached `ConversationMessagesData` entry after a mutation. Cancels any in-flight
 * getMessages fetch first so a poll dispatched before the mutation can't resolve afterwards
 * and overwrite the patch with pre-mutation data. No-ops when there's no cache entry yet:
 * cancelling would kill a cross-surface consumer's initial `fetchIfCacheEmpty` fetch and
 * leave its observer idle, so let that fetch populate from the server instead.
 */
async function patchConversationMessagesData(
  queryClient: QueryClient,
  postData: SatelliteWebLandlordConversationGetMessagesRequestDto,
  apply: (previous: ConversationMessagesData) => ConversationMessagesData,
): Promise<void> {
  const queryKey = getConversationMessagesQueryKey(postData);
  if (queryClient.getQueryData<ConversationMessagesData>(queryKey) === undefined) return;
  await queryClient.cancelQueries({ queryKey });
  queryClient.setQueryData<ConversationMessagesData>(queryKey, (previous) => (previous ? apply(previous) : previous));
}

/**
 * Patch the `note` slice (note save, application invite/marked-sent, tour scheduled) —
 * used by Notepad, useInviteToApply, and ApplicationRequest after their mutations.
 */
export function patchConversationMessagesNote(
  queryClient: QueryClient,
  postData: SatelliteWebLandlordConversationGetMessagesRequestDto,
  notePatch: Partial<ConversationMessagesData['note']>,
): Promise<void> {
  return patchConversationMessagesData(queryClient, postData, (previous) => ({
    ...previous,
    note: { ...previous.note, ...notePatch },
  }));
}

/**
 * Patch the `statusLabel` slice — used by useUpdateStatusLabelCache after tour actions
 * (confirm/cancel/decline) so the inbox NotesBar label updates without waiting for the
 * next poll.
 */
export function patchConversationMessagesStatusLabel(
  queryClient: QueryClient,
  postData: SatelliteWebLandlordConversationGetMessagesRequestDto,
  statusLabelPatch: Partial<ConversationMessagesData['statusLabel']>,
): Promise<void> {
  return patchConversationMessagesData(queryClient, postData, (previous) => ({
    ...previous,
    statusLabel: { ...previous.statusLabel, ...statusLabelPatch },
  }));
}

/**
 * Flag a message's attachments as deleted in the cached latest page — used by
 * DeleteAttachments after its mutation. Patches instead of invalidating: a refetch
 * fired right after the mutation can race the backend's deletion propagation and
 * re-cache the attachment as live.
 */
export function patchConversationMessagesAttachmentsDeleted(
  queryClient: QueryClient,
  postData: SatelliteWebLandlordConversationGetMessagesRequestDto,
  { messageId, zdocIds }: { messageId: string; zdocIds: Array<string> },
): Promise<void> {
  return patchConversationMessagesData(queryClient, postData, (previous) => ({
    ...previous,
    conversation: markMessageAttachmentsDeleted(previous.conversation, messageId, zdocIds),
  }));
}

/**
 * Decide whether a message is an in-flight optimistic temp (sending or failed) that should
 * survive a server refetch. The optimistic id always starts with `temp-` (set by AddMessage),
 * and the `uiTreatmentType` carries a `-sending` or `-failed` suffix while in flight.
 */
const isInFlightTemp = (message: ConversationMessage): boolean => {
  if (!message.messageId?.startsWith('temp-')) return false;
  const treatment = message.uiTreatmentType ?? '';
  return treatment.includes('sending') || treatment.includes('failed');
};

/**
 * Merge in-flight optimistic temps from the previous cache state into a freshly fetched
 * page. Without this, a `getMessages` poll that resolves between the user submitting and
 * the mutation completing (success or error) silently wipes the optimistic bubble — and
 * the mutation's `onError` then has no temp left to flip to a "failed" treatment, so the
 * user loses both the visual feedback and the typed draft.
 *
 * Exported as a pure function so the behavior is unit-testable without a React Query
 * instance.
 */
export function mergeInFlightTemps(
  previous: ConversationMessagesData | undefined,
  fresh: ConversationMessagesData,
): ConversationMessagesData {
  if (!previous) return fresh;
  const freshIds = new Set(fresh.conversation.map((m) => m.messageId));
  const inFlightTemps = previous.conversation.filter((m) => !freshIds.has(m.messageId) && isInFlightTemp(m));
  if (inFlightTemps.length === 0) return fresh;
  return {
    ...fresh,
    conversation: [...fresh.conversation, ...inFlightTemps],
  };
}

/**
 * Decide the next `refetchInterval` value given the current error state and the desired
 * poll interval. Returns `false` (suppress polling) on any 4xx HTTP error — these are
 * non-recoverable for this query (stale conversation, unauthorized user, etc.) and
 * polling against them would just hammer the endpoint forever.
 *
 * Exported as a pure function so the behavior is unit-testable without spinning up a
 * React Query instance.
 */
export function computeRefetchInterval(error: unknown, pollInterval: number | undefined): number | false {
  if (!pollInterval) return false;
  if (is4xxError(error)) return false;
  return pollInterval;
}
